import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Button from '../../components/common/Button';
import {
  getTenantDeletionArchive,
  getTenantDeletionInventory,
  getTenantDeletionJob,
  retryTenantDeletion,
  startTenantDeletion,
} from '../../api/backendApi';

export function canPermanentlyDeleteTenant(role) {
  return role === 'superadmin';
}

const DISPOSITION_LABELS = {
  delete: 'Will delete',
  detach: 'Will detach',
  retain: 'Will retain',
  shared: 'Shared, will not delete',
};

function downloadArchive(archive) {
  const binary = atob(archive.archiveBase64 || '');
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  const blob = new Blob([bytes], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = archive.filename || 'tenant-deletion-archive.zip';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function PathList({ title, paths }) {
  const rows = paths || [];
  if (rows.length === 0) {
    return (
      <p className="text-sm text-gray-600">
        {title}: none
      </p>
    );
  }
  const shown = rows.slice(0, 12);
  return (
    <div>
      <p className="text-sm font-medium text-gray-800">
        {title} ({rows.length})
      </p>
      <ul className="mt-1 text-xs text-gray-600 space-y-1">
        {shown.map((path) => (
          <li key={path}>{path}</li>
        ))}
        {rows.length > shown.length && <li>and {rows.length - shown.length} more</li>}
      </ul>
    </div>
  );
}

export default function TenantDeletionDialog({ tenant, onClose, onFinished }) {
  const tenantId = tenant?.id || tenant?.tenantId;
  const [inventory, setInventory] = useState(null);
  const [inventoryError, setInventoryError] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const [reason, setReason] = useState('');
  const [permanentAck, setPermanentAck] = useState(false);
  const [job, setJob] = useState(null);
  const [working, setWorking] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const expectedName = (inventory?.tenantName || tenant?.name || tenantId || '').trim();
  const nameMatches = confirmName.trim() === expectedName && expectedName.length > 0;
  const terminal = ['completed', 'completed_with_leftovers', 'failed'].includes(job?.status);
  const inProgress = Boolean(job?.jobId) && !terminal;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const response = await getTenantDeletionInventory(tenantId);
        if (cancelled) return;
        setInventory(response?.data || null);
        const jobId = tenant?.deletionJobId;
        if (jobId) {
          const existing = await getTenantDeletionJob(tenantId, jobId);
          if (!cancelled) setJob(existing?.data || null);
        }
      } catch (error) {
        if (!cancelled) setInventoryError('Could not load the pre-delete inventory.');
      }
    }
    if (tenantId) load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, tenant?.deletionJobId]);

  useEffect(() => {
    if (!inProgress || !tenantId || !job?.jobId) return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const response = await getTenantDeletionJob(tenantId, job.jobId);
        if (!cancelled && response?.data) setJob(response.data);
      } catch (error) {
        // The server keeps running. The next poll retries the read.
      }
    }, 2000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [inProgress, tenantId, job?.jobId]);

  const visibleRows = useMemo(() => {
    const rows = inventory?.rows || [];
    return rows.filter((row) => row.count > 0 || row.disposition !== 'delete');
  }, [inventory]);

  async function handleDownload() {
    try {
      setDownloading(true);
      const response = await getTenantDeletionArchive(tenantId);
      const archive = response?.data;
      if (!archive?.archiveBase64 || !archive?.manifest) {
        throw new Error('Archive response did not include a manifest');
      }
      downloadArchive(archive);
      toast.success('Deletion archive downloaded');
    } catch (error) {
      toast.error('Could not download the archive');
    } finally {
      setDownloading(false);
    }
  }

  async function handleStart() {
    if (!nameMatches || !reason.trim() || !permanentAck || inProgress) return;
    try {
      setWorking(true);
      const started = await startTenantDeletion(tenantId, {
        confirmName: confirmName.trim(),
        reason: reason.trim(),
        permanent: true,
      });
      setJob(started?.data || null);
      toast.success('Deletion is running on the server. You can close this page.');
    } catch (error) {
      toast.error('Tenant deletion did not start');
    } finally {
      setWorking(false);
    }
  }

  async function handleRetry() {
    if (!job?.jobId) return;
    try {
      setWorking(true);
      const started = await retryTenantDeletion(tenantId, job.jobId);
      setJob(started?.data || job);
      toast.success('Deletion retry is running on the server.');
    } catch (error) {
      toast.error('Tenant deletion could not be retried');
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" data-testid="tenant-deletion-dialog">
        <div className="p-6 space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800">Delete tenant permanently</h2>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <span className="sr-only">Close</span>
              Close
            </button>
          </div>
          <p className="text-sm text-gray-700">
            Deletion is permanent. Signal Bridge cannot restore {expectedName || tenantId}. Owned records, stored files, and tenant-specific jobs are deleted. Shared phone numbers and shared infrastructure stay. Billing, agreements, support history, and audit records stay with a deleted-tenant tombstone.
          </p>
          <p className="text-sm text-gray-700">
            This is a limited administrative snapshot. It is not a complete backup and cannot be used to restore a deleted tenant.
          </p>
          {inventoryError && <p className="text-sm text-red-700">{inventoryError}</p>}
          {inventory && (
            <div className="border border-gray-200 rounded-md p-3 space-y-2" data-testid="tenant-deletion-inventory">
              {visibleRows.map((row) => (
                <p key={`${row.disposition}-${row.path}`} className="text-sm text-gray-700">
                  {DISPOSITION_LABELS[row.disposition] || row.disposition}: {row.path} ({row.count}
                  {row.measurement && row.measurement !== 'measured' ? `, ${row.measurement}` : ''})
                </p>
              ))}
              {(inventory.storage || []).map((item) => (
                <p key={item.prefix} className="text-sm text-gray-700">
                  Will delete stored files: {item.prefix}
                </p>
              ))}
              <p className="text-sm text-gray-600">
                Shared resources that are never deleted: {(inventory.neverDelete || []).join(', ')}
              </p>
            </div>
          )}
          <div>
            <Button variant="secondary" onClick={handleDownload} disabled={downloading || !inventory} data-testid="tenant-deletion-download">
              {downloading ? 'Preparing archive...' : 'Download archive'}
            </Button>
          </div>
          <label className="block text-sm text-gray-700">
            Type <strong>{expectedName}</strong> to confirm
            <input
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
              value={confirmName}
              onChange={(event) => setConfirmName(event.target.value)}
              data-testid="tenant-deletion-confirm"
              autoComplete="off"
            />
          </label>
          <label className="block text-sm text-gray-700">
            Deletion reason
            <textarea
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              data-testid="tenant-deletion-reason"
              rows={3}
            />
          </label>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="mt-1"
              checked={permanentAck}
              onChange={(event) => setPermanentAck(event.target.checked)}
              data-testid="tenant-deletion-permanent"
            />
            <span>I understand this deletion is permanent and the tenant cannot be restored.</span>
          </label>
          {job && (
            <div className="space-y-2" data-testid="tenant-deletion-progress">
              <p className="text-sm text-gray-800">
                Status: {job.status}. Phase: {job.phase || job.progress?.phase || 'pending'}.
              </p>
              <PathList title="Deleted" paths={job.deleted} />
              <PathList title="Retained" paths={job.retained} />
              <PathList title="Detached" paths={job.detached} />
              <PathList title="Failed" paths={(job.failed || []).map((item) => item.path || item.message || JSON.stringify(item))} />
              <PathList title="Leftovers" paths={job.leftovers} />
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            {job?.status === 'failed' && (
              <Button variant="danger" onClick={handleRetry} disabled={working} data-testid="tenant-deletion-retry">
                {working ? 'Retrying...' : 'Retry deletion'}
              </Button>
            )}
            <Button
              variant="danger"
              onClick={handleStart}
              disabled={!nameMatches || !reason.trim() || !permanentAck || working || !inventory || inProgress}
              data-testid="tenant-deletion-start"
            >
              {working || inProgress ? 'Deletion running on the server' : 'Delete permanently'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
